import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Injector, OnDestroy, OnInit, Optional } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { ModalLayoutRightComponent, NModalLayoutRight } from 'projects/app-default/src/app/ui/modal/modal-layout-right.component';
import { Subject, Subscription } from 'rxjs';
import { NProductViewLayout, Product } from '../../product.decorator';
import { NProductViewUpsellProducts } from '../product-view-upsell-products.namespace';
import { ProductViewUpsellProductsModalItemComponent } from './item/product-view-upsell-products-modal-item.component';
export interface IProductViewUpsellProductsDataContent extends Catalog.IBlockData<IProductViewUpsellProductsDataContent> {}
export interface IProductViewUpsellProductsModalDataContent {
    copy?: string;
}

@DfCmsModal({
    name: 'Product View Upsell Products',
    id: NProductViewUpsellProducts.MODAL_ID,
    position: 'right',
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: NModalLayoutRight.getWidth(sizeName)
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: ['S', 'M', 'L', 'X']
})
@ICmsComponent<ProductViewUpsellProductsModalComponent>({
    id: NProductViewUpsellProducts.MODAL_ID,
    block: {
        url: NProductViewUpsellProducts.MODAL_ID
    },
    onDemandOnly: true,
    name: 'Product View Upsell Products',
    description: 'Product View Upsell Products',
    fields: [
        <NICmsContentField.IField>{
            key: 'copy',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Copy'
        }
    ]
})
@Component({
    templateUrl: './product-view-upsell-products-modal.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        TranslationModule,
        PriceComponent,
        GridModule,
        ModalLayoutRightComponent,
        ProductViewUpsellProductsModalItemComponent
    ]
})
export class ProductViewUpsellProductsModalComponent implements NDfModal.IModalComponent, OnInit, OnDestroy {
    selection?: number[];
    oldSelection?: number[];
    modalConfig?: NDfModal.IModalConfig;
    selectionSubject = new Subject<void>();
    replacements = {};
    total = 0;
    confirmed = false;
    private subscriptions = new Subscription();
    @ICmsContent({
        type: 'blockContent'
    })
    modalContent?: IProductViewUpsellProductsModalDataContent;
    constructor(
        readonly client: Client,
        readonly injector: Injector,
        private changeDetectorRef: ChangeDetectorRef,
        private app: App,
        @Optional()
        private modalRef: ModalRef<ProductViewUpsellProductsModalComponent>,
        @Optional()
        @Inject(MODAL_DATA)
        private data: NProductViewUpsellProducts.IModalData
    ) {}
    ngOnInit(): void {
        this.selection = this.data.selection;
        this.oldSelection = Array.from(this.selection);
        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.detectChanges()));
        this.updateTotalPrice();
        this.detectChanges();
    }
    ngOnDestroy(): void {
        if (!this.confirmed) {
            this.oldSelection && this.data.onConfirm({ selection: this.oldSelection });
            this.selectionSubject.next();
        }
        this.subscriptions.unsubscribe();
    }
    confirm(): void {
        this.confirmed = true;
        this.selection && this.data.onConfirm({ selection: this.selection, replacements: this.replacements });
        this.close();
    }
    select(id: number | undefined): void {
        id && this.selection?.push(id);
        this.updateTotalPrice();
        this.selectionSubject.next();
        this.detectChanges();
    }
    unselect(id: number): void {
        this.selection = this.selection?.filter(i => i !== id);
        this.updateTotalPrice();
        this.selectionSubject.next();
        this.detectChanges();
    }
    close(): void {
        this.modalRef.close();
    }
    get groups(): NProductViewUpsellProducts.IUpsellGroup[] {
        return this.data.groups;
    }
    get content(): NProductViewLayout.IBlockDataContent {
        return this.data.content;
    }
    trackByProductId(index: number, product: Product): number {
        return product.id;
    }
    trackByGroupId(index: number, group: NProductViewUpsellProducts.IUpsellGroup): string {
        return group.id;
    }
    detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    get label(): string {
        return this.modalContent?.copy || this.data.label;
    }

    updateTotalPrice(): void {
        this.total =
            this.selection?.reduce((acc, id) => {
                const product = this.app.resources.product.get(id);
                return product ? acc + product.price : acc;
            }, 0) || 0;
    }
}
